import React, { Fragment, useState } from 'react'
import {
  PlusCircle as PlusCircleIcon,
  Trash2 as Trash2Icon,
  UploadCloud as UploadCloudIcon,
} from 'react-feather'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import clsx from 'clsx'
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core'

import { AlertDialog } from 'components'

import { DetailsPlanForm, SummaryFormItems } from './components'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import theme from 'theme'

import useSnackbar from 'hooks/useSnackbar'
import useStyles from './styles'

const EvidenceQuestion = ({
  evidencePlanAction,
  evidencePlanActionId,
  changesManagementId,
  componentIndex,
  disabledInputs,
  inputFileRef,
  setLoading,
}) => {
  const { filterAccepted } = helpers.file

  const name = `evidencePlanActions[${componentIndex}]`

  const [openDialog, setOpenDialog] = useState(false)
  const [planActionData, setPlanActionData] = useState({})

  const snackbar = useSnackbar()
  const classes = useStyles()

  const { control, errors, watch, getValues, reset } = useFormContext()
  const { fields, append } = useFieldArray({
    control,
    name: `${name}.planActions`,
  })

  const attachments = watch(`${name}.evidence.attachments`)

  const componentError = errors?.evidencePlanActions?.[componentIndex]

  const handleInputFile = () => inputFileRef?.current?.click()

  const handleAddPlanAction = () => {
    append({
      responsible: null,
      deadlineDate: null,
      what: '',
      how: '',
      because: '',
      where: '',
      howPrice: '',
      status: constants.planAction.INITIAL_STATUS_ID,
    })
  }

  const handleOpenDialog = () => setOpenDialog(true)
  const handleCloseDialog = () => {
    setPlanActionData({})
    setOpenDialog(false)
  }

  const handleRemovePlanActionSafely = (index) => {
    const formValues = getValues()
    const structured = helpers.functions.unflattenObject(formValues)
    const structuredEvidencePlanActions =
      structured?.evidencePlanActions?.[componentIndex]

    const currentEvidenceQuestion = {
      title: evidencePlanAction?.title,
      description: evidencePlanAction?.description,
      ...(structuredEvidencePlanActions ?? {}),
    }

    const newPlanActions = (currentEvidenceQuestion?.planActions || []).filter(
      (_, i) => i !== index,
    )

    delete currentEvidenceQuestion.planActions

    const newEvidenceQuestion = {
      ...currentEvidenceQuestion,
      planActions: newPlanActions,
    }

    const newEvidencePlanActions = [...structured.evidencePlanActions]
    newEvidencePlanActions[componentIndex] = newEvidenceQuestion

    reset({
      ...structured,
      evidencePlanActions: newEvidencePlanActions,
    })
  }

  const handleValidationPlanAction = (index, originId) => {
    if (originId) {
      setPlanActionData({ index, originId })
      handleOpenDialog()
    } else {
      handleRemovePlanActionSafely(index)
    }
  }

  const handleDeletePlanAction = async () => {
    const originId = planActionData?.originId
    if (originId && evidencePlanActionId) {
      try {
        setLoading(true)
        await service.dponet.changesManagement.destroyPlanAction({
          changesManagementId,
          evidencePlanActionId,
          planActionId: originId,
        })
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(
            error?.response?.data?.error,
          ),
          variant: 'error',
        })
        setLoading(false)
        return
      } finally {
        setLoading(false)
      }
    }
    handleRemovePlanActionSafely(planActionData?.index)
    handleCloseDialog()
  }

  return (
    <Fragment>
      {!disabledInputs && (
        <Box>
          <Box className={classes.attachmentButtonContainer}>
            <Box display="flex" flexDirection="column" width="100%">
              <Controller
                as={
                  <Button
                    color="primary"
                    variant="contained"
                    startIcon={<UploadCloudIcon />}
                    className={clsx(classes.attachmentButton, {
                      [classes.errorButton]:
                        !!componentError?.evidence?.attachments,
                    })}
                    onClick={handleInputFile}
                    disabled={disabledInputs}
                    fullWidth
                  >
                    <input
                      type="file"
                      disabled={disabledInputs}
                      ref={inputFileRef}
                      className={classes.inputFile}
                      accept={constants.validations.PDF_FILE_TYPE}
                      multiple
                    />
                    Selecionar Arquivos
                  </Button>
                }
                control={control}
                name={`evidencePlanActions[${componentIndex}].evidence.attachments`}
                onChange={([event]) =>
                  filterAccepted(
                    event,
                    attachments,
                    constants.validations.PDF_FILE_TYPE,
                  )
                }
              />
            </Box>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<PlusCircleIcon />}
              className={clsx(
                classes.attachmentButton,
                classes.planActionButton,
              )}
              onClick={handleAddPlanAction}
              disabled={disabledInputs}
              fullWidth
            >
              Adicionar plano de ação
            </Button>
          </Box>
          {!!componentError?.evidence?.attachments && (
            <FormHelperText error={!!componentError?.evidence?.attachments}>
              {<>{componentError?.evidence?.attachments?.message}</>}
            </FormHelperText>
          )}
        </Box>
      )}
      {fields?.map((planAction, index) => (
        <Paper key={planAction?.id} className={classes.planActionPaper}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            minHeight={60}
          >
            <Typography variant="h4">Plano de ação</Typography>
            {!disabledInputs && (
              <IconButton
                disabled={disabledInputs}
                onClick={() =>
                  handleValidationPlanAction(index, planAction?.originId)
                }
              >
                <Trash2Icon size={20} />
              </IconButton>
            )}
          </Box>
          <Grid container spacing={3}>
            <SummaryFormItems
              name={name}
              index={index}
              planAction={planAction}
              componentIndex={componentIndex}
              disabled={disabledInputs}
            />
            <DetailsPlanForm
              name={name}
              index={index}
              componentIndex={componentIndex}
              disabled={disabledInputs}
            />
          </Grid>
        </Paper>
      ))}
      <AlertDialog
        open={openDialog}
        onClose={handleCloseDialog}
        variant="standard"
        severity="warning"
        content="Tem certeza de que deseja excluir este plano de ação? Essa ação não poderá ser desfeita!"
        dialogActions={
          <>
            <Button
              variant="outlined"
              color="primary"
              className={classes.outlinedWarningButton}
              onClick={handleCloseDialog}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.containerWarningButton}
              onClick={handleDeletePlanAction}
            >
              Excluir
            </Button>
          </>
        }
      />
    </Fragment>
  )
}

export default EvidenceQuestion
